import React, { useState } from 'react';
import { useUserStore } from '../stores/useUserStores';
import { useCartStore } from '../stores/useCartStores';
import { useNavigate, useLocation } from 'react-router-dom';
import './CSS/LoginSignup.css';

const LoginSignup = () => {
  const [floatingMessage, setFloatingMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });

  const { login, signup, getUser } = useUserStore();
  const { addToCart } = useCartStore();

  const navigate = useNavigate();
  const location = useLocation();

  // Where the user came from (default "/")
  const from = location.state?.from?.pathname || '/';
  const postLoginAction = location.state?.action || null;

  const showFloatingMessage = (message) => {
    setFloatingMessage(message);
    setTimeout(() => setFloatingMessage(''), 2500);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleLogin = async () => {
    const { email, password } = formData;
    if (!email || !password) {
      showFloatingMessage('Please fill in all fields.');
      return;
    }

    setIsLoading(true);

    try {
      await login(email, password);
      await getUser(); // refresh global user state

      // If there was a pending add-to-cart action before login, perform it
      if (postLoginAction?.type === 'addToCart') {
        const { productId, quantity, size } = postLoginAction;
        if (!size) {
          showFloatingMessage('Please select a size for the product.');
          navigate(from, { replace: true });
          setIsLoading(false);
          return;
        }
        try {
          await addToCart(productId, quantity, size);
          showFloatingMessage('Item added to cart successfully!');
        } catch (err) {
          console.error('Auto add-to-cart failed', err);
        }
      }

      setTimeout(() => {
        setIsLoading(false);
        navigate(from, { replace: true });
      }, 1500);
    } catch (error) {
      console.error(error);
      showFloatingMessage('Login failed. Please check your credentials.');
      setIsLoading(false);
    }
  };

  const handleSignup = async () => {
    const { email, password, confirmPassword, name } = formData;
    if (!email || !password || !confirmPassword || !name) {
      showFloatingMessage('Please fill in all fields.');
      return;
    }
    if (password !== confirmPassword) {
      showFloatingMessage('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      await signup(email, password, name, 'customer');
      setTimeout(() => {
        setIsLoading(false);
        showFloatingMessage('Signup successful! Please log in.');
        setIsLogin(true);
      }, 1500);
    } catch (error) {
      console.error(error);
      showFloatingMessage('Signup failed. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="loginsignup">
      <div className="loginsignup-container" role="dialog" aria-labelledby="loginTitle">
        <h1 id="loginTitle" className="loginsignup-title">
          {isLogin ? 'Log in to Your Account' : 'Create an Account'}
        </h1>

        <div className="loginsignup-fields">
          {!isLogin && (
            <div className="input-wrapper">
              <input
                type="text"
                name="name"
                placeholder="YOUR NAME"
                value={formData.name}
                onChange={handleInputChange}
                aria-label="Full name"
              />
            </div>
          )}

          <div className="input-wrapper">
            <input
              type="email"
              name="email"
              placeholder="E-MAIL"
              value={formData.email}
              onChange={handleInputChange}
              aria-label="Email address"
            />
          </div>

          <div className="input-wrapper">
            <input
              type="password"
              name="password"
              placeholder="PASSWORD"
              value={formData.password}
              onChange={handleInputChange}
              aria-label="Password"
            />
          </div>

          {!isLogin && (
            <div className="input-wrapper">
              <input
                type="password"
                name="confirmPassword"
                placeholder="CONFIRM PASSWORD"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                aria-label="Confirm password"
              />
            </div>
          )}
        </div>

        <div className="forgot-password" style={{ width: '100%' }}>
        <span onClick={() => navigate('/forgot-password')} role="button" tabIndex={0}>
        Forgot password?
       </span>
        </div>

        <button
          type="button"
          className="loginsignup-button"
          onClick={isLogin ? handleLogin : handleSignup}
          disabled={isLoading}
        >
          {isLogin ? 'LOG IN' : 'SIGN UP'}
        </button>

        <p className="loginsignup-toggle">
          {isLogin ? "Don't have an account? " : 'Already have an account? '}
          <span
            onClick={() => setIsLogin((s) => !s)}
            role="button"
            tabIndex={0}
            aria-pressed={!isLogin}
          >
            {isLogin ? 'Sign Up here' : 'Login here'}
          </span>
        </p>
      </div>

      {/* Floating Message */}
      {floatingMessage && <div className="floating-message">{floatingMessage}</div>}

      {/* Loading Popup */}
      {isLoading && (
        <div className="loading-popup show">
          <div className="loading-box">
            <div className="loading-spinner"></div>
            <p className="loading-text">
              {isLogin ? 'Logging in, please wait...' : 'Signing up, please wait...'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginSignup;
